import { createApi } from '@reduxjs/toolkit/query/react';
import { baseQuery } from './general';

/** POST `/supplier-companies/evaluation/:token` */
export type ScoreCardEvaluationFieldSubmit = {
    type: string;
    id: number;
    answer: string | number | boolean | string[] | null;
};

export type ScoreCardEvaluationSectionSubmit = {
    id: number;
    fields: ScoreCardEvaluationFieldSubmit[];
};

export type ScoreCardEvaluationSubmitBody = {
    sections: ScoreCardEvaluationSectionSubmit[];
    user_name: string;
};

/** GET `/supplier-companies/evaluation/:token/check` */
export type ScoreCardEvaluationCheckResponse = {
    data?: {
        company?: {
            name?: string | null;
        };
        bayer?: {
            city?: string | null;
            province?: string | null;
        };
    };
};

export type UpdateSupplierPricingBody = {
    price: number;
    basis: 'year' | 'month';
};

export type UpdateSupplierPricingArg = {
    tender_id: string | number;
    supplier_id: string | number;
    data: UpdateSupplierPricingBody;
};

export const tendersApi = createApi({
    reducerPath: 'tendersApi',
    baseQuery,
    // TODO: add types for endpoints
    endpoints: (builder) => ({
        createArea: builder.mutation({
            query: (data) => {
                return {
                    url: '/admin/tenders/expense-areas',
                    method: 'POST',
                    formData: true,
                    body: data
                };
            }
        }),
        updateArea: builder.mutation({
            query: (data) => {
                return {
                    url: `/admin/tenders/expense-areas/${data?.id}`,
                    method: 'PUT',
                    formData: true,
                    body: data.data
                };
            }
        }),
        deleteArea: builder.mutation({
            query: (id) => {
                return {
                    url: `/admin/tenders/expense-areas/${id}`,
                    method: 'Delete'
                };
            }
        }),
        createTender: builder.mutation({
            query: (data) => {
                return {
                    url: '/admin/tenders',
                    method: 'POST',
                    body: data
                };
            }
        }),
        getTender: builder.query<any, any>({
            query: (id) => ({
                url: `/tenders/${id}`,
                method: 'GET'
            })
        }),

        updateTender: builder.mutation({
            query: (data) => {
                return {
                    url: `/admin/tenders/${data?.id}`,
                    method: 'PUT',
                    formData: true,
                    body: data.data
                };
            }
        }),
        updateDeadline: builder.mutation({
            query: (data) => {
                return {
                    url: `/tenders/${data.id}/update-deadline`,
                    method: 'PUT',
                    formData: true,
                    body: data.data
                };
            }
        }),
        updateTaskDeadline: builder.mutation({
            query: (data) => {
                return {
                    url: `tenders/${data.id}/tasks/update-deadline`,
                    method: 'PUT',
                    formData: true,
                    body: data.data
                };
            }
        }),
        completeTask: builder.mutation({
            query: (data) => {
                return {
                    url: `/tenders/${data.tenderId}/tasks/${data.taskId}/complete`,
                    method: 'PUT',
                    formData: true
                };
            }
        }),
        approveTask: builder.mutation({
            query: (data) => {
                return {
                    url: `/tenders/${data.tenderId}/tasks/${data.taskId}/approve`,
                    method: 'PUT'
                };
            }
        }),
        declineTask: builder.mutation({
            query: (data) => {
                return {
                    url: `/tenders/${data.ids.tenderId}/tasks/${data.ids.taskId}/decline`,
                    method: 'PUT',
                    body: data.data
                };
            }
        }),
        sendTask: builder.mutation({
            query: (data) => {
                return {
                    url: `tenders/${data.id}/tasks/send`,
                    method: 'POST',
                    body: data.data
                };
            }
        }),
        moveToNextPhase: builder.mutation({
            query: (id) => {
                return {
                    url: `/tenders/${id}/update-phase`,
                    method: 'PUT'
                };
            }
        }),
        UpdateTask: builder.mutation({
            query: (data) => {
                return {
                    url: `/tenders/${data.ids.tender_id}/tasks/${data.ids.task_id}`,
                    method: 'PUT',
                    body: data.data
                };
            }
        }),
        createSupportingDocument: builder.mutation({
            query: (data) => {
                return {
                    url: `/tenders/${data.ids.tenderId}/tasks`,
                    method: 'POST',
                    body: data.data
                };
            }
        }),
        sendActivities: builder.mutation({
            query: (data) => {
                return {
                    url: `tenders/${data.tenderId}/tasks/${data.taskId}/activities`,
                    method: 'POST',
                    body: data.data
                };
            }
        }),
        deleteFile: builder.mutation({
            query: (data) => {
                return {
                    url: `tenders/${data.tenderId}/tasks/${data.taskId}/files/${data.file}`,
                    method: 'Delete'
                };
            }
        }),
        deleteComment: builder.mutation({
            query: (data) => {
                return {
                    url: `tenders/${data.tenderId}/tasks/${data.taskId}`,
                    method: 'Delete'
                };
            }
        }),
        downloadFile: builder.query<any, any>({
            query: (data) => ({
                url: `tenders/${data.tenderId}/tasks/${data.taskId}/files/${data.fileId}/download`,
                method: 'GET'
            })
        }),

        fileManagerDownload: builder.query<Blob, { signature: string }>({
            query: ({ signature }) => ({
                url: 'file-manager/download',
                method: 'GET',
                params: { signature },
                headers: {
                    Accept: 'application/pdf,application/octet-stream,*/*'
                },
                responseHandler: async (response) => response.blob()
            })
        }),
        resetAction: builder.mutation<any, any>({
            query: (data) => {
                return {
                    url: `tenders/${data.tenderId}/tasks/${data.taskId}/reset-action`,
                    method: 'POST',
                    body: data.data
                };
            }
        }),
        addSupplier: builder.mutation<any, any>({
            query: (data) => {
                return {
                    url: `tenders/${data.tenderId}/supplier-candidates`,
                    method: 'POST',
                    body: data.data
                };
            }
        }),

        updateSupplier: builder.mutation<any, any>({
            query: (data) => {
                return {
                    url: `tenders/${data.tenderId}/supplier-candidates/${data.supplierId}`,
                    method: 'PUT',
                    body: data.data
                };
            }
        }),
        updateSupplierOnGates: builder.mutation<any, any>({
            query: (data) => {
                return {
                    url: `tenders/${data.tenderId}/suppliers/${data.supplierId}`,
                    method: 'PUT',
                    body: data.data
                };
            }
        }),
        deleteManualySupplier: builder.mutation({
            query: (data) => ({
                url: `/tenders/${data.tender_id}/supplier-candidates/${data.invite_id}`,
                method: 'DELETE'
            })
        }),

        rejectSupplier: builder.mutation<any, any>({
            query: (data) => {
                return {
                    url: `tenders/${data.tenderId}/suppliers/${data.supplierId}/reject`,
                    method: 'POST',
                    body: data.data
                };
            }
        }),
        shortlistSupplier: builder.mutation<any, any>({
            query: (data) => {
                return {
                    url: `tenders/${data.tenderId}/suppliers/${data.supplierId}/shortlist`,
                    method: 'POST',
                    body: data.data
                };
            }
        }),
        deleteSupplier: builder.mutation<any, any>({
            query: (data) => {
                return {
                    url: `tenders/${data.tenderId}/suppliers/${data.supplierId}`,
                    method: 'DELETE',
                    body: data.data
                };
            }
        }),
        deleteSupplierAward: builder.mutation<any, any>({
            query: (data) => {
                return {
                    url: `tenders/${data.tenderId}/suppliers/award-list/${data.supplierId}`,
                    method: 'DELETE',
                    body: data.data
                };
            }
        }),
        checkTenderPitch: builder.query<any, any>({
            query: (signature) => ({
                url: `/supplier-companies/tender-pitch?${signature}`,
                method: 'GET'
            })
        }),
        submitPitch: builder.mutation<any, any>({
            query: (data) => {
                return {
                    url: `supplier-companies/tender-pitch?signature=${data?.signature}`,
                    method: 'POST',
                    body: data.data
                };
            }
        }),
        managePitch: builder.mutation<any, any>({
            query: (data) => {
                return {
                    url: `supplier-companies/tender-pitch/decline?signature=${data?.signature}`,
                    method: 'POST',
                    body: data.data
                };
            }
        }),
        deleteSupplierInvite: builder.mutation<any, any>({
            query: (data) => {
                return {
                    url: `tenders/${data.tender_id}/supplier-companies/delete-invite/${data.invite_id}`,
                    method: 'DELETE',
                    body: data.data
                };
            }
        }),
        resendInviteSupplierCompanies: builder.mutation<any, any>({
            query: (data) => {
                return {
                    url: `tenders/${data.tender_id}/supplier-companies/${data.invite_id}/send-invite`,
                    method: 'POST'
                };
            }
        }),
        UpdateTaskDate: builder.mutation({
            query: (data) => {
                return {
                    url: `/tenders/${data.tender_id}/tasks/${data.task_id}?_method=PUT`,
                    method: 'POST',
                    body: data.data
                };
            }
        }),
        createTask: builder.mutation({
            query: (data) => {
                return {
                    url: `/tenders/${data.tenderId}/tasks`,
                    method: 'POST',
                    body: data.data
                };
            }
        }),
        cancelTender: builder.mutation({
            query: (data) => {
                return {
                    url: `/tenders/${data.tenderId}/cancel`,
                    method: 'PUT',
                    body: { comment: data.comment }
                };
            }
        }),

        getArchive: builder.query<any, any>({
            query: (params) => ({
                url: `tenders/archive`,
                method: 'GET',
                params
            })
        }),
        getArchiveEA: builder.query<any, void>({
            query: () => ({
                url: `tenders/archive-expense-areas`,
                method: 'GET'
            })
        }),
        getNotInvitedSuppliers: builder.query<any, any>({
            query: (data) => ({
                url: `/tenders/${data.tender_id}/supplier-companies/not-invited`,
                method: 'GET',
                params: data.filter
            })
        }),
        getConfirmedSuppliers: builder.query<any, any>({
            query: (data) => ({
                url: `/tenders/${data.tender_id}/supplier-companies/confirmed`,
                method: 'GET',
                params: data.filter
            })
        }),
        getAwardPhaseSuppliers: builder.query<any, any>({
            query: (data) => ({
                url: `/tenders/${data.tender_id}/suppliers/award-list`,
                method: 'GET',
                params: data.filter
            })
        }),
        getQuestionaryForm: builder.query<any, any>({
            query: (data) => ({
                url: `/tenders/${data?.tender_id}/tasks/${data?.task_id}/kick-off-questionnaire`,
                method: 'GET',
                params: data?.step ? { step: data.step } : {}
            })
        }),
        sendInvitesToSuppliers: builder.mutation({
            query: (data) => {
                return {
                    url: `/tenders/${data.tender_id}/supplier-companies/send-invites`,
                    method: 'POST',
                    body: data.data
                };
            }
        }),
        resendInvitesToSuppliers: builder.mutation({
            query: (data) => {
                return {
                    url: `/tenders/${data.tender_id}/supplier-candidates/${data?.supp_id}/send-invite`,
                    method: 'POST'
                };
            }
        }),
        addSuppliersToTheTender: builder.mutation({
            query: (data) => {
                return {
                    url: `/tenders/${data.tender_id}/suppliers`,
                    method: 'POST',
                    body: data.data
                };
            }
        }),
        markLinkAsOpened: builder.mutation({
            query: (data) => {
                return {
                    url: `/tenders/${data.tender_id}/tasks/${data.task_id}/activities/supply-agreement`,
                    method: 'POST',
                    body: data.data
                };
            }
        }),
        addSuppliersToAwardPhase: builder.mutation({
            query: (data) => {
                return {
                    url: `/tenders/${data.tender_id}/suppliers/award-list`,
                    method: 'POST',
                    body: data.data
                };
            }
        }),
        saveKikOffForm: builder.mutation({
            query: (data) => {
                const { tender_id, task_id, step, action, formData } = data;

                const params = new URLSearchParams();
                if (step) params.append('step', step);
                if (action) params.append('action', 'next_step');

                return {
                    url: `/tenders/${tender_id}/tasks/${task_id}/kick-off-questionnaire${
                        params.toString() ? `?${params}` : ''
                    }`,
                    method: 'POST',
                    body: formData
                };
            }
        }),
        discardKikOffFormChanges: builder.mutation<
            unknown,
            { tender_id: number | string; task_id: string | number; step: number | string }
        >({
            query: ({ tender_id, task_id, step }) => {
                const params = new URLSearchParams();
                if (step != null && step !== '') {
                    params.append('step', String(step));
                }
                return {
                    url: `/tenders/${tender_id}/tasks/${task_id}/kick-off-questionnaire/discard-changes${
                        params.toString() ? `?${params}` : ''
                    }`,
                    method: 'POST'
                };
            }
        }),
        uploadFile: builder.mutation({
            query: (data) => {
                const { tender_id, task_id, step, formData } = data;

                const params = new URLSearchParams();
                if (step) params.append('step', step);

                return {
                    url: `/tenders/${tender_id}/tasks/${task_id}/kick-off-questionnaire/upload-file${
                        params.toString() ? `?${params}` : ''
                    }`,
                    method: 'POST',
                    body: formData
                };
            }
        }),
        resendForm: builder.mutation({
            query: (data) => {
                return {
                    url: `/tenders/${data.tender_id}/tasks/resend`,
                    method: 'POST',
                    body: data.data
                };
            }
        }),
        restartPhase: builder.mutation({
            query: (data) => {
                return {
                    url: `/tenders/${data.tender_id}/restart-phase`,
                    method: 'POST'
                };
            }
        }),
        updateIncumbent: builder.mutation({
            query: (data) => {
                return {
                    url: `/tenders/${data?.tender_id}/supplier-companies/update-incumbent/${data?.invite_id}`,
                    method: 'PUT',
                    body: data.data
                };
            }
        }),

        supplyAgreement: builder.mutation({
            query: (data) => {
                return {
                    url: `/tenders/${data?.ids?.tender_id}/suppliers/${data?.ids?.supplier_id}/supply-agreement`,
                    method: 'PUT',
                    body: data.data
                };
            }
        }),
        updateIncumbentInTender: builder.mutation({
            query: (data) => {
                return {
                    url: `/tenders/${data?.tender_id}/suppliers/${data.supplier_id}/update-incumbent`,
                    method: 'PUT',
                    body: data.data
                };
            }
        }),
        updateManager: builder.mutation({
            query: (data) => {
                return {
                    url: `/tenders/${data?.tender_id}/supplier-companies/update-manager/${data?.invite_id}`,
                    method: 'PUT',
                    body: data.data
                };
            }
        }),
        getScoreCard: builder.query({
            query: (data) => {
                console.log('data', data);
                return {
                    url: `/supplier-companies/evaluation/${data?.token}?user_name=${data?.user_name}`,
                    method: 'GET'
                };
            }
        }),
        submitScoreCardEvaluation: builder.mutation<unknown, { token: string; body: ScoreCardEvaluationSubmitBody }>({
            query: ({ token, body }) => ({
                url: `/supplier-companies/evaluation/${token}`,
                method: 'POST',
                body
            })
        }),

        /** GET `/supplier-companies/evaluation/:token/check` */
        checkScoreCardEvaluation: builder.query<ScoreCardEvaluationCheckResponse, string>({
            query: (token) => ({
                url: `/supplier-companies/evaluation/${token}/check`,
                method: 'GET'
            })
        }),

        deleteScoreCardResponse: builder.mutation<unknown, { tender_id: string | number; response: string | number }>({
            query: ({ tender_id, response }) => ({
                url: `/tenders/${tender_id}/supplier-companies/score-cord/${response}`,
                method: 'DELETE'
            })
        }),
        updateGrade: builder.mutation({
            query: (data) => {
                return {
                    url: `/tenders/${data?.tender_id}/suppliers/${data?.supplier_id}/update-grade`,
                    method: 'PUT',
                    body: data.data
                };
            }
        }),
        reschedulePitch: builder.mutation({
            query: (data) => {
                return {
                    url: `/tenders/${data?.tender_id}/suppliers/${data?.supplier_id}/pitch/${data?.pitch_id}/reschedule`,
                    method: 'PUT',
                    body: data?.data
                };
            }
        }),
        updateSupplierPricing: builder.mutation<unknown, UpdateSupplierPricingArg>({
            query: ({ tender_id, supplier_id, data }) => ({
                url: `/tenders/${tender_id}/suppliers/${supplier_id}/pricing`,
                method: 'PUT',
                body: data
            })
        })
    })
});

export const {
    useCreateAreaMutation,
    useUpdateAreaMutation,
    useDeleteAreaMutation,
    useCreateTenderMutation,
    useLazyGetTenderQuery,
    useUpdateTenderMutation,
    useUpdateDeadlineMutation,
    useCompleteTaskMutation,
    useDeclineTaskMutation,
    useMoveToNextPhaseMutation,
    useUpdateTaskMutation,
    useSendTaskMutation,
    useApproveTaskMutation,
    useSendActivitiesMutation,
    useDeleteFileMutation,
    useCreateSupportingDocumentMutation,
    useUpdateTaskDeadlineMutation,
    useLazyDownloadFileQuery,
    useLazyFileManagerDownloadQuery,
    useResetActionMutation,
    useAddSupplierMutation,
    useUpdateSupplierMutation,
    useRejectSupplierMutation,
    useShortlistSupplierMutation,
    useDeleteSupplierMutation,
    useDeleteManualySupplierMutation,
    useUpdateTaskDateMutation,
    useCreateTaskMutation,
    useCancelTenderMutation,
    useLazyGetArchiveQuery,
    useLazyGetArchiveEAQuery,
    useLazyGetNotInvitedSuppliersQuery,
    useSendInvitesToSuppliersMutation,
    useDeleteSupplierInviteMutation,
    useUpdateIncumbentMutation,
    useUpdateManagerMutation,
    useLazyGetConfirmedSuppliersQuery,
    useAddSuppliersToTheTenderMutation,
    useUpdateIncumbentInTenderMutation,
    useUpdateGradeMutation,
    useResendFormMutation,
    useLazyGetAwardPhaseSuppliersQuery,
    useAddSuppliersToAwardPhaseMutation,
    useDeleteSupplierAwardMutation,
    useSupplyAgreementMutation,
    useMarkLinkAsOpenedMutation,
    useRestartPhaseMutation,
    useLazyGetQuestionaryFormQuery,
    useDiscardKikOffFormChangesMutation,
    useSaveKikOffFormMutation,
    useUploadFileMutation,
    useResendInvitesToSuppliersMutation,
    useResendInviteSupplierCompaniesMutation,
    useCheckTenderPitchQuery,
    useSubmitPitchMutation,
    useManagePitchMutation,
    useReschedulePitchMutation,
    useGetScoreCardQuery,
    useLazyGetScoreCardQuery,
    useSubmitScoreCardEvaluationMutation,
    useCheckScoreCardEvaluationQuery,
    useLazyCheckScoreCardEvaluationQuery,
    useDeleteScoreCardResponseMutation,
    useUpdateSupplierOnGatesMutation,
    useUpdateSupplierPricingMutation
} = tendersApi;
